export function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const s = Math.max(0, Math.round(seconds));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  const pad = secs < 10 ? '0' : '';
  return `${mins}:${pad}${secs}`;
}

export function loadVideoMetadata(
  file: File
): Promise<{ seconds: number | null; resolution: string | null }> {
  return new Promise((resolve) => {
    const videoEl = document.createElement('video');
    const url = URL.createObjectURL(file);
    videoEl.preload = 'metadata';
    videoEl.src = url;
    const cleanup = () => {
      URL.revokeObjectURL(url);
      videoEl.removeAttribute('src');
    };
    const fail = () => {
      cleanup();
      resolve({ seconds: null, resolution: null });
    };
    videoEl.onloadedmetadata = () => {
      const seconds = isNaN(videoEl.duration) ? null : videoEl.duration;
      const resolution =
        videoEl.videoWidth && videoEl.videoHeight
          ? `${videoEl.videoWidth}x${videoEl.videoHeight}`
          : null;
      cleanup();
      resolve({ seconds, resolution });
    };
    videoEl.onerror = fail;
  });
}

export function extractVideoThumbnail(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    let settled = false;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      resolve(null);
      return;
    }

    const videoEl = document.createElement('video');
    videoEl.style.position = 'fixed';
    videoEl.style.top = '0';
    videoEl.style.left = '-9999px';
    videoEl.style.width = '1px';
    videoEl.style.height = '1px';
    videoEl.preload = 'auto';
    videoEl.muted = true;
    videoEl.playsInline = true;
    const url = URL.createObjectURL(file);
    videoEl.src = url;
    document.body.appendChild(videoEl);

    const timeoutId = window.setTimeout(() => finish(null), 8000);

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      URL.revokeObjectURL(url);
      videoEl.pause();
      videoEl.removeAttribute('src');
      videoEl.load();
      videoEl.remove();
    };

    function finish(result: Blob | null) {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    }

    const drawFrame = () => {
      try {
        if (videoEl.readyState < 2) {
          finish(null);
          return;
        }
        const width = videoEl.videoWidth || 640;
        const height = videoEl.videoHeight || 360;
        if (!width || !height) {
          finish(null);
          return;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(videoEl, 0, 0, width, height);
        canvas.toBlob((blob) => {
          finish(blob);
        }, 'image/jpeg', 0.8);
      } catch (e) {
        finish(null);
      }
    };

    const seekAndCapture = () => {
      try {
        const duration = videoEl.duration;
        const seekTarget =
          isFinite(duration) && duration > 2 ? Math.min(1, duration / 2) : 0.1;
        if (seekTarget > 0) {
          videoEl.currentTime = seekTarget;
          videoEl.onseeked = drawFrame;
        } else {
          drawFrame();
        }
      } catch (e) {
        finish(null);
      }
    };

    // Prefer requestVideoFrameCallback (guarantees a freshly rendered frame)
    // over drawing right after seeked, which can race the decoder. Request it
    // while playing — pausing first would stop new frames and stall the callback.
    const captureWithRvf = () => {
      try {
        const rvf = (videoEl as any).requestVideoFrameCallback;
        if (typeof rvf === 'function') {
          rvf.call(videoEl, () => {
            videoEl.pause();
            drawFrame();
          });
          return;
        }
      } catch (e) {
        // fall through to seek path
      }
      seekAndCapture();
    };

    // Force frame decode by playing (muted), then pause before drawing.
    const playAndCapture = () => {
      try {
        const playPromise = videoEl.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(captureWithRvf)
            .catch(() => seekAndCapture());
        } else {
          captureWithRvf();
        }
      } catch (e) {
        seekAndCapture();
      }
    };

    videoEl.oncanplay = playAndCapture;
    videoEl.onloadeddata = playAndCapture;
    videoEl.onerror = () => finish(null);
  });
}
